<template>
  <v-dialog v-model="waiverRequiredDialog" width="500">
    <v-card>
      <v-card-title class="text-h5 grey lighten-2">
        {{ $t("registerEvent.waiver_information") }}
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12">
            {{ $t("registerEvent.waiver_information_message") }}
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" v-for="waiver in waiversUser" :key="waiver.eventWaiverId">
            <v-card>
              <v-card-title>
                {{ waiver.waiverTranslate[$i18n.locale] ? waiver.waiverTranslate[$i18n.locale].title : waiver.waiverTranslate.first.title }}
              </v-card-title>
              <v-card-text>
                <v-btn 
                  v-if="waiver.userId == userStore.id || !waiver.age18"
                  color="primary" 
                  elevation="2" 
                  @click="signOrSendWaiver(waiver)"
                  :disabled="isSignedStatus(waiver)"
                >
                  {{ !isSignedStatus(waiver) ? $t("registerEvent.sign_now") : $t("registerEvent.signed") }}
                </v-btn>
                <template v-else-if="hasLivelapsEmail(waiver)">
                  <p class="mb-2">{{ $t('registerEvent.provide_email_for', { name: waiver.fullName }) }}</p>
                  <v-text-field
                    v-model="enterEmailByWaiver[waiverKey(waiver)]"
                    :label="$t('waivers.signatory_email')"
                    type="email"
                    outlined
                    dense
                    :error-messages="enterEmailErrors[waiverKey(waiver)] || ''"
                    @input="$set(enterEmailErrors, waiverKey(waiver), '')"
                  ></v-text-field>
                  <v-btn
                    color="primary"
                    elevation="2"
                    :loading="enterEmailSaving[waiverKey(waiver)] === true"
                    :disabled="!(enterEmailByWaiver[waiverKey(waiver)] || '').trim() || enterEmailSaving[waiverKey(waiver)] === true"
                    @click="submitEmailUpdate(waiver)"
                  >
                    {{ $t('registerEvent.update_email') }}
                  </v-btn>
                </template>
                <BtnSendEmail
                  v-else
                  :waiver="waiver"
                  :disabled="!canSendEmailStatus(waiver)"
                  @sendWaiver="signOrSendWaiver"
                  :componentFather="componentFather"
                ></BtnSendEmail>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>        
        <v-btn color="primary" text @click="waiverRequiredDialog = false">
          {{ $t('common.close') }}
        </v-btn>
        <v-btn v-if="showSignWaiverNowButton" :color="buttonColor" class="white--text" @click="signWaiverNow">
          {{ $t('registerEvent.sign_waiver_now') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex";
import { VDialog, VCard, VCardTitle, VCardText, VRow, VCol, VDivider, VCardActions, VSpacer, VBtn, VTextField } from 'vuetify/lib';
import EventWaiverService from "@/services/EventWaiverService";
import WaiverService from "@/services/WaiverService";
import BtnSendEmail from "@/components/eventRegistration/components/BtnSendEmail.vue";
import AuthService from "@/services/AuthService";
import ProfileService from "@/services/ProfileService";
import { isLivelapsEmail } from "@/utils/emailUtils";

export default {
  name: "WaiverDialog",
  components: {
    VDialog, VCard, VCardTitle, VCardText, VRow, VCol, VDivider, VCardActions, VSpacer, VBtn, VTextField, BtnSendEmail
  },
  props: {
    promoterName: { type: String, default: '' },
    role: { type: String, default: 'Spectator' },
    waiverDialog: { type: Boolean, default: false },
    bSend: { type: Boolean, default: false },
    componentFather: { type: String, defaultl: ''}
  },
  data() {
    return {
      showSignWaiverNowButton: false,
      loadingSignWaiverButton: false,
      pendingWaiverStatusRefresh: false,
      checkingWaiverStatusRefresh: false,
      waiverStatusRefreshIntervalId: null,
      enterEmailByWaiver: {},
      enterEmailErrors: {},
      enterEmailSaving: {}
    };
  },
  computed: {
    ...mapState({
      waiversUser: (state) => state.waiverDialog.waiversUser,
      userStore: (state) => state.session.user,
      buttonColor: (state) => state.env.get('VUE_APP_BUTTON_COLOR'),
      waiverData: (state) => state.waiverDialog.waiverData
    }),
    waiverRequiredDialog: {
      get() {
        return this.waiverDialog;
      },
      set(value) { 
        this.$emit('changeField', { field: 'waiverRequiredDialog', value: value });
      }
    },
    messageWaiverInformation() {
      return this.showSignWaiverNowButton
        ? this.$t("registerEvent.waiver_information_message")
        : this.$t("registerEvent.waiver_information_message_adult");
    },
    bSendWaiver: {
      get() {
        return this.bSend;
      },
      set(value) {
        this.$emit('changeField', { field: 'bSendWaiver', value: value });
      }
    },
    targetParticipantId() {
      if (this.waiverData && this.waiverData.userId) {
        return this.waiverData.userId;
      }
      if (Array.isArray(this.waiversUser) && this.waiversUser.length > 0 && this.waiversUser[0].userId) {
        return this.waiversUser[0].userId;
      }
      return null;
    }
  },
  watch: {
    waiverDialog: {
      immediate: true,
      handler(value) {
        if (value) {
          this.checkSignWaiverNowAvailability();
        } else {
          this.showSignWaiverNowButton = false;
        }
      }
    }
  },
  mounted() {
    window.addEventListener("focus", this.handleWindowFocusAfterWaiverSign);
  },
  beforeDestroy() {
    window.removeEventListener("focus", this.handleWindowFocusAfterWaiverSign);
    this.clearWaiverStatusRefreshInterval();
  },
  methods: {
    normalizeWaiverStatus(waiver) {
      const raw = (waiver?.waiverstatus || waiver?.waiverStatus || "").toString().trim().toLowerCase();
      if (raw === "signed" || raw === "2" || raw === "4") return "signed";
      if (raw === "notsigned" || raw === "not signed") return "notsigned";
      if (raw === "rejected" || raw === "3") return "rejected";
      return raw;
    },
    isSignedStatus(waiver) {
      return this.normalizeWaiverStatus(waiver) === "signed";
    },
    canSendEmailStatus(waiver) {
      const status = this.normalizeWaiverStatus(waiver);
      return status === "notsigned" || status === "rejected";
    },
    getBackendMessageKey(message) {
      if (typeof message === 'string' && message.length > 0) {
        return message;
      }
      if (Array.isArray(message) && message.length > 0 && typeof message[0] === 'string') {
        return message[0];
      }
      return null;
    },
    resolveBackendMessage(message) {
      const key = this.getBackendMessageKey(message);
      if (!key) {
        return this.$t('common.error_general');
      }

      const namespaces = ['eventWaiver', 'registerEvent', 'common'];
      const translationKey = namespaces.find((ns) => this.$te(`${ns}.${key}`));
      return translationKey ? this.$t(`${translationKey}.${key}`) : this.$t('common.error_general');
    },
    async checkSignWaiverNowAvailability() {
      this.showSignWaiverNowButton = false;

      if (!this.targetParticipantId || this.loadingSignWaiverButton) {
        return;
      }

      this.loadingSignWaiverButton = true;
      try {
        const response = await WaiverService.getHasOrganizationWaiversToSign({
          participantId: this.targetParticipantId
        });
        this.showSignWaiverNowButton = !!(response?.data?.success && response?.data?.message);
      } catch (error) {
        this.showSignWaiverNowButton = false;
      } finally {
        this.loadingSignWaiverButton = false;
      }
    },
    async signWaiverNow(){
      if (!this.targetParticipantId) {
        this.$emit('alertNotValidate', this.$t('common.error_general'));
        return;
      }

      try {
        const response = await WaiverService.getOrCreateOrganizationWaiverToSign({
          participantId: this.targetParticipantId
        });

        if (response?.data?.success && response?.data?.message?.waiverLink) {
          const waiverUrl = `${this.$store.getters.env.get('VUE_APP_LIVELAPS_HOST')}${response.data.message.waiverLink}`;
          const waiverWindow = window.open(waiverUrl, "_blank");
          this.waiverRequiredDialog = false;
          this.pendingWaiverStatusRefresh = true;
          this.startWaiverStatusRefreshWatcher(waiverWindow);
          return;
        }

        this.$emit('alertNotValidate', this.resolveBackendMessage(response?.data?.message || 'waiver_not_found'));
      } catch (error) {
        this.$emit('alertNotValidate', this.resolveBackendMessage(error?.response?.data?.message));
      }
    },
    clearWaiverStatusRefreshInterval() {
      if (!this.waiverStatusRefreshIntervalId) {
        return;
      }

      clearInterval(this.waiverStatusRefreshIntervalId);
      this.waiverStatusRefreshIntervalId = null;
    },
    startWaiverStatusRefreshWatcher(waiverWindow) {
      this.clearWaiverStatusRefreshInterval();

      if (!waiverWindow) {
        this.refreshWaiverStatusAvailability();
        return;
      }

      this.waiverStatusRefreshIntervalId = setInterval(() => {
        if (!waiverWindow.closed) {
          return;
        }

        this.refreshWaiverStatusAvailability();
      }, 1500);
    },
    handleWindowFocusAfterWaiverSign() {
      this.refreshWaiverStatusAvailability();
    },
    async refreshWaiverStatusAvailability() {
      if (!this.pendingWaiverStatusRefresh || this.checkingWaiverStatusRefresh) {
        return;
      }

      this.checkingWaiverStatusRefresh = true;
      this.clearWaiverStatusRefreshInterval();

      try {
        await this.checkSignWaiverNowAvailability();
        this.$emit('refreshWaiverStatus');
      } finally {
        this.pendingWaiverStatusRefresh = false;
        this.checkingWaiverStatusRefresh = false;
      }
    },
    waiverKey(waiver) {
      return waiver.eventWaiverId || waiver.fkWaiverId || waiver.userId || 0;
    },
    hasLivelapsEmail(waiver) {
      return isLivelapsEmail(waiver.participantEmail);
    },
    async submitEmailUpdate(waiver) {
      const key = this.waiverKey(waiver);
      const email = (this.enterEmailByWaiver[key] || '').trim();
      if (!email) return;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.$set(this.enterEmailErrors, key, this.$t('waivers.enter_email_dialog_text'));
        return;
      }

      this.$set(this.enterEmailSaving, key, true);
      this.$set(this.enterEmailErrors, key, '');

      try {
        const dupResp = await AuthService.preventDuplicateEmail(email);
        if (dupResp.data && dupResp.data.match) {
          this.$set(this.enterEmailErrors, key, this.$t('waivers.email_already_in_use'));
          this.$set(this.enterEmailSaving, key, false);
          return;
        }

        const userId = Number(waiver.userId || 0);
        if (!userId) {
          this.$set(this.enterEmailErrors, key, this.$t('common.error_general'));
          this.$set(this.enterEmailSaving, key, false);
          return;
        }

        const authUserId = Number(this.userStore?.id || 0);
        const updateResp = await ProfileService.updateEmail({ userId, email, authUserId });

        if (updateResp.data && updateResp.data.success === 1) {
          if (updateResp.data.message === 'unique_email') {
            this.$set(this.enterEmailErrors, key, this.$t('waivers.email_already_in_use'));
            this.$set(this.enterEmailSaving, key, false);
            return;
          }
          this.$set(waiver, 'participantEmail', email);
          this.$emit('showAlert', { msg: this.$t('waivers.email_updated_success'), color: 'success' });
        } else {
          this.$set(this.enterEmailErrors, key, this.$t('common.error_general'));
        }
      } catch (e) {
        this.$set(this.enterEmailErrors, key, this.$t('common.error_general'));
      }
      this.$set(this.enterEmailSaving, key, false);
    },
    signOrSendWaiver(waiver) {
      this.generateWaiver(waiver.eventWaiverId, waiver.userId, this.role);
    },
    async generateWaiver(eventWaiverId, userId, role) {
      try {
        const response = await EventWaiverService.generateWaiverByEventWaiverId({
          eventWaiverId,
          userId,
          role
        });

        if (response.data.success) {
          this.handleWaiverSuccess(response.data.message);
        } else {
          this.handleWaiverError(response.data.message);
        }
      } catch (error) {
        this.$emit('alertNotValidate', this.$t('common.error_general'));
      }
    },
    handleWaiverSuccess(message) {
      if (message.id) {
        this.$emit('addWaiver', message);
      } else {
        this.bSendWaiver = true;
        this.$emit('showAlert', { msg: this.$t('registerEvent.send_email_successfully'), color: 'success' });
      }
    },
    handleWaiverError(message) {
      this.$emit('alertNotValidate', this.resolveBackendMessage(message));
    }
  }
}
</script>